import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Plus, X, Lock, LogOut, Calendar, Home, Settings, Trash2, Edit3, Check, Eye, EyeOff, Image } from 'lucide-react';
import headerLogo from './assets/coastalcards_logo.png';

// Helper function to convert 24-hour time to 12-hour format
const formatTime = (time) => {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

// Parse date-only values as local dates instead of UTC.
const parseLocalDate = (dateString) => {
  if (!dateString) return null;

  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Header logo
const LOGO_URL = headerLogo;

export default function App() {
  const [events, setEvents] = useState([]);
  const [carouselSlides, setCarouselSlides] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchEvents = useCallback(async () => {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .order('date', { ascending: true })
      .order('start_time', { ascending: true });

    if (error) throw error;

    const mapped = (data || []).map((row) => ({
      id: row.id,
      title: row.title,
      date: row.date,
      startTime: (row.start_time || '').slice(0, 5),
      endTime: (row.end_time || '').slice(0, 5),
      description: row.description || '',
      color: row.color || '#4A90D9',
    }));

    setEvents(mapped);
  }, []);

  const fetchSlides = useCallback(async () => {
    const { data, error } = await supabase
      .from('carousel_slides')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    const mapped = (data || []).map((row) => ({
      id: row.id,
      image: row.image_url,
      title: row.title || '',
      subtitle: row.subtitle || '',
      sortOrder: row.sort_order ?? 0,
    }));

    setCarouselSlides(mapped);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoadError('');
    setIsLoading(true);
    try {
      await Promise.all([fetchSlides(), fetchEvents()]);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load data from Supabase.');
    } finally {
      setIsLoading(false);
    }
  }, [fetchSlides, fetchEvents]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  return (
    <div style={styles.app}>
      <SiteHeader />

      <main style={styles.main}>
        {isLoading && (
          <div style={styles.noticeBanner}>
            Loading…
          </div>
        )}
        {!isLoading && loadError && (
          <div style={{ ...styles.noticeBanner, backgroundColor: '#FFF2F2', borderColor: '#FFD1D1', color: '#B42318' }}>
            {loadError}
          </div>
        )}

        <Routes>
          <Route path="/" element={<HomePage carouselSlides={carouselSlides} events={events} />} />
          <Route path="/calendar" element={<CalendarPage events={events} />} />
          <Route
            path="/admin"
            element={
              <AdminPage
                events={events}
                setEvents={setEvents}
                carouselSlides={carouselSlides}
                setCarouselSlides={setCarouselSlides}
                refreshEvents={fetchEvents}
                refreshSlides={fetchSlides}
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer style={styles.footer}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
          <p style={styles.footerText}><strong>Address:</strong> 2201 1st Ave S, St. Petersburg, FL 33712 (located inside Coastal Creative)</p>
          <p style={styles.footerText}><strong>Hours:</strong> Tuesday–Saturday 11am–8pm · Sunday 12pm–8pm</p>
          <p style={styles.footerText}><strong>Phone:</strong> <a href="tel:8134529799" style={{ color: '#8FA3B8', textDecoration: 'none' }}>813 452 9799</a></p>
          <p style={{ ...styles.footerText, marginTop: '10px' }}>© 2025 Coastal Cards. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function SiteHeader() {
  return (
    <header style={styles.header}>
      <div style={styles.headerContent}>
        <div style={styles.logoContainer}>
          <img src={LOGO_URL} alt="Coastal Cards" style={styles.logoImage} />
        </div>

        <nav style={styles.nav}>
          <NavLink
            to="/"
            end
            style={({ isActive }) => ({
              ...styles.navButton,
              ...(isActive ? styles.navButtonActive : {}),
              textDecoration: 'none',
            })}
          >
            <Home size={18} />
            <span>Home</span>
          </NavLink>

          <NavLink
            to="/calendar"
            style={({ isActive }) => ({
              ...styles.navButton,
              ...(isActive ? styles.navButtonActive : {}),
              textDecoration: 'none',
            })}
          >
            <Calendar size={18} />
            <span>Calendar</span>
          </NavLink>
        </nav>
      </div>
    </header>
  );
}

// ==================== HOME PAGE ====================
function HomePage({ carouselSlides, events }) {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (carouselSlides.length <= 1) return;
    const timer = setInterval(() => {
      if (!isAnimating) {
        setIsAnimating(true);
        setTimeout(() => {
          setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
          setIsAnimating(false);
        }, 500);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [carouselSlides.length, isAnimating]);

  const goToSlide = (index) => {
    if (!isAnimating && index !== currentSlide) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide(index);
        setIsAnimating(false);
      }, 300);
    }
  };

  const nextSlide = () => {
    if (carouselSlides.length <= 1) return;
    goToSlide((currentSlide + 1) % carouselSlides.length);
  };
  const prevSlide = () => {
    if (carouselSlides.length <= 1) return;
    goToSlide((currentSlide - 1 + carouselSlides.length) % carouselSlides.length);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingEvents = events
    .filter((event) => parseLocalDate(event.date) >= today)
    .sort(
      (a, b) =>
        parseLocalDate(a.date).getTime() -
        parseLocalDate(b.date).getTime()
    )
    .slice(0, 4);

  return (
    <div>
      {/* Full-width Carousel Banner */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 24px',
      }}>
        <div style={{
          position: 'relative',
          borderRadius: '20px',
          overflow: 'hidden',
          marginTop: '24px',
          boxShadow: '0 12px 40px rgba(74, 144, 217, 0.15)',
        }}>
          <div style={{
            width: '100%',
            height: '500px',
            backgroundColor: '#f0f4f8',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: isAnimating ? 0.7 : 1,
          }}>
            {carouselSlides.length > 0 ? (
            <img 
              src={carouselSlides[currentSlide].image} 
              alt={carouselSlides[currentSlide].title || 'Carousel slide'}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <div style={{ padding: '24px', color: '#8FA3B8' }}>
              No carousel slides yet.
            </div>
          )}
          </div>
          
          {/* Carousel Controls */}
          <button onClick={prevSlide} style={{...styles.carouselArrow, left: '20px'}}>
            <ChevronLeft size={32} />
          </button>
          <button onClick={nextSlide} style={{...styles.carouselArrow, right: '20px'}}>
            <ChevronRight size={32} />
          </button>
          
          {/* Dots */}
          <div style={styles.carouselDots}>
            {carouselSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToSlide(idx)}
                style={{
                  ...styles.carouselDot,
                  ...(idx === currentSlide ? styles.carouselDotActive : {})
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Rest of homepage content */}
      <div style={styles.homePage}>
      {/* Upcoming Events Preview */}
      <section style={styles.eventsPreview}>
        <h2 style={styles.sectionTitle}>Upcoming Events</h2>
        <div style={styles.eventsGrid}>
          {upcomingEvents.map(event => (
            <div key={event.id} style={styles.eventPreviewCard}>
              <div style={{...styles.eventColorBar, backgroundColor: event.color}} />
              <div style={styles.eventPreviewContent}>
                <h4 style={styles.eventPreviewTitle}>{event.title}</h4>
                <p style={styles.eventPreviewDate}>
                  {parseLocalDate(event.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                  {event.startTime && event.endTime && (
                    <span> • {formatTime(event.startTime)} - {formatTime(event.endTime)}</span>
                  )}
                </p>
                <p style={styles.eventPreviewDesc}>{event.description}</p>
              </div>
            </div>
          ))}
        </div>
        <button onClick={() => navigate('/calendar')} style={styles.viewAllBtn}>
          View Full Calendar
        </button>
      </section>
      </div>
    </div>
  );
}

// ==================== CALENDAR PAGE ====================
function CalendarPage({ events }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewMode, setViewMode] = useState('month');

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startingDay = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToToday = () => setCurrentDate(new Date());

  const getEventsForDate = (date) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const days = [];
  for (let i = 0; i < startingDay; i++) {
    days.push(<div key={`empty-${i}`} style={styles.calendarDayEmpty} />);
  }
  for (let day = 1; day <= totalDays; day++) {
    const dayEvents = getEventsForDate(day);
    const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
    const isSelected = selectedDate === day;
    
    days.push(
      <div
        key={day}
        onClick={() => setSelectedDate(day === selectedDate ? null : day)}
        style={{
          ...styles.calendarDay,
          ...(isToday ? styles.calendarDayToday : {}),
          ...(isSelected ? styles.calendarDaySelected : {}),
        }}
      >
        <span style={styles.dayNumber}>{day}</span>
        {dayEvents.length > 0 && (
          <div style={styles.dayEventsContainer}>
            {dayEvents.slice(0, 2).map(event => (
              <div key={event.id} style={{...styles.dayEvent, backgroundColor: event.color}}>
                {event.title}
              </div>
            ))}
            {dayEvents.length > 2 && (
              <div style={styles.moreEvents}>+{dayEvents.length - 2} more</div>
            )}
          </div>
        )}
      </div>
    );
  }

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div style={styles.calendarPage}>
      <div style={styles.calendarHeader}>
        <div style={styles.calendarNav}>
          <button onClick={prevMonth} style={styles.calendarNavBtn}>
            <ChevronLeft size={24} />
          </button>
          <h2 style={styles.calendarMonth}>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
          <button onClick={nextMonth} style={styles.calendarNavBtn}>
            <ChevronRight size={24} />
          </button>
        </div>
        <button onClick={goToToday} style={styles.todayBtn}>Today</button>
      </div>

      <div style={styles.calendarGrid}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={styles.calendarWeekday}>{day}</div>
        ))}
        {days}
      </div>

      {/* Selected Date Events Panel */}
      {selectedDate && (
        <div style={styles.selectedDatePanel}>
          <h3 style={styles.selectedDateTitle}>
            {new Date(year, month, selectedDate).toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric'
            })}
          </h3>
          {selectedEvents.length > 0 ? (
            <div style={styles.selectedDateEvents}>
              {selectedEvents.map(event => (
                <div key={event.id} style={styles.selectedEventCard}>
                  <div style={{...styles.selectedEventColor, backgroundColor: event.color}} />
                  <div style={styles.selectedEventInfo}>
                    <h4 style={styles.selectedEventTitle}>{event.title}</h4>
                    {event.startTime && event.endTime && (
                      <p style={styles.selectedEventTime}>{formatTime(event.startTime)} - {formatTime(event.endTime)}</p>
                    )}
                    <p style={styles.selectedEventDesc}>{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={styles.noEvents}>No events scheduled for this day</p>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== ADMIN PAGE ====================
function AdminPage({ events, setEvents, carouselSlides, setCarouselSlides, refreshEvents, refreshSlides }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState('');
  const [opError, setOpError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', startTime: '', endTime: '', description: '', color: '#4A90D9' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeTab, setActiveTab] = useState('events');

  const [editingSlide, setEditingSlide] = useState(null);
  const [newSlide, setNewSlide] = useState({ image: '', title: '', subtitle: '' });
  const [showAddSlideForm, setShowAddSlideForm] = useState(false);

  const mapEventRow = (row) => ({
    id: row.id,
    title: row.title,
    date: row.date,
    startTime: (row.start_time || '').slice(0, 5),
    endTime: (row.end_time || '').slice(0, 5),
    description: row.description || '',
    color: row.color || '#4A90D9',
  });

  const mapSlideRow = (row) => ({
    id: row.id,
    image: row.image_url,
    title: row.title || '',
    subtitle: row.subtitle || '',
    sortOrder: row.sort_order ?? 0,
  });

  // Handle PKCE OAuth redirect (code=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    (async () => {
      setAuthLoading(true);
      const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
      if (error) setAuthError(error.message);
      // Clean up URL (remove ?code=...)
      window.history.replaceState({}, document.title, '/admin');
      setAuthLoading(false);
    })();
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      const { data } = await supabase.auth.getSession();
      if (!isMounted) return;
      setSession(data.session);
      setAuthLoading(false);
    })();

    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      setAuthLoading(false);
    });

    return () => {
      isMounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  // Domain guard (UI-level). RLS is the real enforcement.
  useEffect(() => {
    const email = session?.user?.email?.toLowerCase() || '';
    if (!email) return;

    if (!email.endsWith('@coastalcreativetv.com')) {
      setAuthError('This Google account is not authorized for Coastal Cards admin access.');
      supabase.auth.signOut();
    }
  }, [session]);

  const signInWithGoogle = async () => {
    setAuthError('');
    setOpError('');
    setIsBusy(true);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/admin`,
        queryParams: {
          hd: 'coastalcreativetv.com',
          prompt: 'select_account',
        },
      },
    });

    if (error) setAuthError(error.message);
    setIsBusy(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleAddEvent = async () => {
    setOpError('');
    if (!newEvent.title || !newEvent.date || !newEvent.startTime || !newEvent.endTime) {
      setOpError('Please fill in Title, Date, Start Time, and End Time.');
      return;
    }

    setIsBusy(true);
    const payload = {
      title: newEvent.title,
      date: newEvent.date,
      start_time: newEvent.startTime,
      end_time: newEvent.endTime,
      description: newEvent.description || '',
      color: newEvent.color || '#4A90D9',
    };

    const { data, error } = await supabase.from('events').insert([payload]).select('*').single();
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    setEvents((prev) => [...prev, mapEventRow(data)].sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime)));
    setNewEvent({ title: '', date: '', startTime: '', endTime: '', description: '', color: '#4A90D9' });
    setShowAddForm(false);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    setOpError('');
    setIsBusy(true);

    const payload = {
      title: editingEvent.title,
      date: editingEvent.date,
      start_time: editingEvent.startTime,
      end_time: editingEvent.endTime,
      description: editingEvent.description || '',
      color: editingEvent.color || '#4A90D9',
    };

    const { error } = await supabase.from('events').update(payload).eq('id', editingEvent.id);
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    setEvents((prev) =>
      prev
        .map((ev) => (ev.id === editingEvent.id ? { ...editingEvent } : ev))
        .sort((a, b) => (a.date + a.startTime).localeCompare(b.date + b.startTime))
    );
    setEditingEvent(null);
  };

  const handleDeleteEvent = async (id) => {
    setOpError('');
    setIsBusy(true);
    const { error } = await supabase.from('events').delete().eq('id', id);
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    setEvents((prev) => prev.filter((ev) => ev.id !== id));
  };

  const handleAddSlide = async () => {
    setOpError('');
    if (!newSlide.image || !newSlide.title) {
      setOpError('Please provide an Image URL and Title.');
      return;
    }

    setIsBusy(true);

    const payload = {
      image_url: newSlide.image,
      title: newSlide.title,
      subtitle: newSlide.subtitle || '',
      sort_order: carouselSlides.length,
    };

    const { data, error } = await supabase.from('carousel_slides').insert([payload]).select('*').single();
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    setCarouselSlides((prev) => [...prev, mapSlideRow(data)].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)));
    setNewSlide({ image: '', title: '', subtitle: '' });
    setShowAddSlideForm(false);
  };

  const handleUpdateSlide = async () => {
    if (!editingSlide) return;

    setOpError('');
    setIsBusy(true);

    const payload = {
      image_url: editingSlide.image,
      title: editingSlide.title,
      subtitle: editingSlide.subtitle || '',
    };

    const { error } = await supabase.from('carousel_slides').update(payload).eq('id', editingSlide.id);
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    setCarouselSlides((prev) => prev.map((s) => (s.id === editingSlide.id ? { ...editingSlide } : s)));
    setEditingSlide(null);
  };

  const persistSlideOrder = async (slidesInOrder) => {
    // Update only the sort_order column for existing rows
    const updates = slidesInOrder.map((s, idx) => ({ id: s.id, sort_order: idx }));
    const { error } = await supabase.from('carousel_slides').upsert(updates, { onConflict: 'id' });
    if (error) setOpError(error.message);
  };

  const handleDeleteSlide = async (id) => {
    if (carouselSlides.length <= 1) return;

    setOpError('');
    setIsBusy(true);
    const { error } = await supabase.from('carousel_slides').delete().eq('id', id);
    setIsBusy(false);

    if (error) {
      setOpError(error.message);
      return;
    }

    const remaining = carouselSlides.filter((s) => s.id !== id);
    setCarouselSlides(remaining);
    await persistSlideOrder(remaining);
    await refreshSlides();
  };

  const moveSlide = async (id, direction) => {
    const index = carouselSlides.findIndex((slide) => slide.id === id);
    if (index === -1) return;
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === carouselSlides.length - 1)) return;

    const newSlides = [...carouselSlides];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newSlides[index], newSlides[swapIndex]] = [newSlides[swapIndex], newSlides[index]];

    setCarouselSlides(newSlides);
    await persistSlideOrder(newSlides);
    await refreshSlides();
  };

  // ========= Login Screen =========
  if (authLoading) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <div style={styles.loginHeader}>
            <Lock size={48} style={{ color: '#4A90D9' }} />
            <h2 style={styles.loginTitle}>Admin Access</h2>
            <p style={styles.loginSubtitle}>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={styles.loginContainer}>
        <div style={styles.loginCard}>
          <div style={styles.loginHeader}>
            <Lock size={48} style={{ color: '#4A90D9' }} />
            <h2 style={styles.loginTitle}>Admin Access</h2>
            <p style={styles.loginSubtitle}>Sign in with your CoastalCreativeTV Google account.</p>
          </div>

          {authError && <p style={styles.loginError}>{authError}</p>}

          <button type="button" onClick={signInWithGoogle} style={styles.loginBtn} disabled={isBusy}>
            {isBusy ? 'Redirecting…' : 'Sign in with Google'}
          </button>

          <div style={{ marginTop: '16px', color: '#8FA3B8', fontSize: '13px', lineHeight: 1.4 }}>
            <div><strong>URL:</strong> /admin</div>
            <div><strong>Allowed domain:</strong> coastalcreativetv.com</div>
          </div>
        </div>
      </div>
    );
  }

  // ========= Admin Dashboard =========
  const signedInEmail = session?.user?.email || '';

  return (
    <div style={styles.adminContainer}>
      <div style={styles.adminHeader}>
        <div>
          <h2 style={styles.adminTitle}>
            <Settings size={28} style={{ marginRight: '12px' }} />
            Admin Dashboard
          </h2>
          <p style={{ margin: '6px 0 0 0', color: '#8FA3B8', fontSize: '13px' }}>
            Signed in as <span style={{ fontFamily: 'monospace', color: '#4A90D9' }}>{signedInEmail}</span>
          </p>
        </div>

        <button onClick={handleLogout} style={styles.logoutBtn}>
          <LogOut size={18} />
          Sign Out
        </button>
      </div>

      {opError && (
        <div style={{ ...styles.noticeBanner, backgroundColor: '#FFF2F2', borderColor: '#FFD1D1', color: '#B42318' }}>
          {opError}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('events')}
          style={{ ...styles.tab, ...(activeTab === 'events' ? styles.tabActive : {}) }}
        >
          <Calendar size={18} />
          Calendar Events
        </button>
        <button
          onClick={() => setActiveTab('carousel')}
          style={{ ...styles.tab, ...(activeTab === 'carousel' ? styles.tabActive : {}) }}
        >
          <Image size={18} />
          Carousel Slides
        </button>
      </div>

      {/* Events Tab */}
      {activeTab === 'events' && (
        <>
          <button onClick={() => setShowAddForm(true)} style={styles.addEventBtn}>
            <Plus size={20} />
            Add New Event
          </button>

          {showAddForm && (
            <div style={styles.modal}>
              <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Add New Event</h3>
                  <button onClick={() => setShowAddForm(false)} style={styles.modalClose}>
                    <X size={24} />
                  </button>
                </div>

                <div style={styles.eventForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Event Title *</label>
                    <input
                      type="text"
                      value={newEvent.title}
                      onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                      style={styles.input}
                      placeholder="Enter event title"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Date *</label>
                    <input
                      type="date"
                      value={newEvent.date}
                      onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.timeRow}>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>Start Time *</label>
                      <input
                        type="time"
                        value={newEvent.startTime}
                        onChange={(e) => setNewEvent({ ...newEvent, startTime: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>End Time *</label>
                      <input
                        type="time"
                        value={newEvent.endTime}
                        onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Description</label>
                    <textarea
                      value={newEvent.description}
                      onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                      placeholder="Enter event description"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Color</label>
                    <div style={styles.colorPicker}>
                      {['#4A90D9', '#E74C3C', '#27AE60', '#9B59B6', '#F39C12', '#1ABC9C'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setNewEvent({ ...newEvent, color })}
                          style={{
                            ...styles.colorOption,
                            backgroundColor: color,
                            ...(newEvent.color === color ? styles.colorOptionSelected : {}),
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={styles.formActions}>
                    <button type="button" onClick={() => setShowAddForm(false)} style={styles.cancelBtn}>
                      Cancel
                    </button>
                    <button type="button" onClick={handleAddEvent} style={styles.saveBtn} disabled={isBusy}>
                      <Check size={18} />
                      {isBusy ? 'Saving…' : 'Add Event'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Events List */}
          <div style={styles.eventsList}>
            {events.map((event) => (
              <div key={event.id} style={styles.eventCard}>
                <div style={{ ...styles.eventCardColor, backgroundColor: event.color }} />
                <div style={styles.eventCardContent}>
                  <div style={styles.eventCardHeader}>
                    <h3 style={styles.eventCardTitle}>{event.title}</h3>
                    <div style={styles.eventCardActions}>
                      <button onClick={() => setEditingEvent({ ...event })} style={styles.iconBtn}>
                        <Edit3 size={18} />
                      </button>
                      <button onClick={() => handleDeleteEvent(event.id)} style={{ ...styles.iconBtn, ...styles.deleteBtn }}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <p style={styles.eventCardDate}>
                    {parseLocalDate(event.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    <span style={{ marginLeft: '8px', color: '#8FA3B8' }}>
                      {formatTime(event.startTime)} - {formatTime(event.endTime)}
                    </span>
                  </p>
                  <p style={styles.eventCardDesc}>{event.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Event Modal */}
          {editingEvent && (
            <div style={styles.modal}>
              <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Edit Event</h3>
                  <button onClick={() => setEditingEvent(null)} style={styles.modalClose}>
                    <X size={24} />
                  </button>
                </div>

                <div style={styles.eventForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Event Title *</label>
                    <input
                      type="text"
                      value={editingEvent.title}
                      onChange={(e) => setEditingEvent({ ...editingEvent, title: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Date *</label>
                    <input
                      type="date"
                      value={editingEvent.date}
                      onChange={(e) => setEditingEvent({ ...editingEvent, date: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.timeRow}>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>Start Time *</label>
                      <input
                        type="time"
                        value={editingEvent.startTime}
                        onChange={(e) => setEditingEvent({ ...editingEvent, startTime: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.inputLabel}>End Time *</label>
                      <input
                        type="time"
                        value={editingEvent.endTime}
                        onChange={(e) => setEditingEvent({ ...editingEvent, endTime: e.target.value })}
                        style={styles.input}
                      />
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Description</label>
                    <textarea
                      value={editingEvent.description}
                      onChange={(e) => setEditingEvent({ ...editingEvent, description: e.target.value })}
                      style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Color</label>
                    <div style={styles.colorPicker}>
                      {['#4A90D9', '#E74C3C', '#27AE60', '#9B59B6', '#F39C12', '#1ABC9C'].map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditingEvent({ ...editingEvent, color })}
                          style={{
                            ...styles.colorOption,
                            backgroundColor: color,
                            ...(editingEvent.color === color ? styles.colorOptionSelected : {}),
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div style={styles.formActions}>
                    <button type="button" onClick={() => setEditingEvent(null)} style={styles.cancelBtn}>
                      Cancel
                    </button>
                    <button type="button" onClick={handleUpdateEvent} style={styles.saveBtn} disabled={isBusy}>
                      <Check size={18} />
                      {isBusy ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Carousel Tab */}
      {activeTab === 'carousel' && (
        <>
          <button onClick={() => setShowAddSlideForm(true)} style={styles.addEventBtn}>
            <Plus size={20} />
            Add New Slide
          </button>

          {showAddSlideForm && (
            <div style={styles.modal}>
              <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Add New Slide</h3>
                  <button onClick={() => setShowAddSlideForm(false)} style={styles.modalClose}>
                    <X size={24} />
                  </button>
                </div>

                <div style={styles.eventForm}>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Image URL *</label>
                    <input
                      type="url"
                      value={newSlide.image}
                      onChange={(e) => setNewSlide({ ...newSlide, image: e.target.value })}
                      style={styles.input}
                      placeholder="https://..."
                    />
                    <p style={styles.inputHint}>Paste a Cloudinary (or other) image URL.</p>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Title *</label>
                    <input
                      type="text"
                      value={newSlide.title}
                      onChange={(e) => setNewSlide({ ...newSlide, title: e.target.value })}
                      style={styles.input}
                      placeholder="Slide title"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Subtitle</label>
                    <input
                      type="text"
                      value={newSlide.subtitle}
                      onChange={(e) => setNewSlide({ ...newSlide, subtitle: e.target.value })}
                      style={styles.input}
                      placeholder="Optional subtitle"
                    />
                  </div>

                  <div style={styles.formActions}>
                    <button type="button" onClick={() => setShowAddSlideForm(false)} style={styles.cancelBtn}>
                      Cancel
                    </button>
                    <button type="button" onClick={handleAddSlide} style={styles.saveBtn} disabled={isBusy}>
                      <Check size={18} />
                      {isBusy ? 'Saving…' : 'Add Slide'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Slides List */}
          <div style={styles.eventsList}>
            {carouselSlides.map((slide, idx) => (
              <div key={slide.id} style={styles.eventCard}>
                <div style={styles.slideThumb}>
                  <img src={slide.image} alt={slide.title} style={styles.slideThumbImg} />
                </div>

                <div style={styles.eventCardContent}>
                  <div style={styles.eventCardHeader}>
                    <div style={styles.slideInfo}>
                      <h3 style={styles.eventCardTitle}>{slide.title}</h3>
                      <p style={styles.eventCardDesc}>{slide.subtitle}</p>
                      <p style={styles.slideUrl} title={slide.image}>
                        {slide.image}
                      </p>
                    </div>

                    <div style={styles.eventCardActions}>
                      <button onClick={() => moveSlide(slide.id, 'up')} style={styles.iconBtn} disabled={idx === 0}>
                        <ChevronUp size={18} />
                      </button>
                      <button
                        onClick={() => moveSlide(slide.id, 'down')}
                        style={styles.iconBtn}
                        disabled={idx === carouselSlides.length - 1}
                      >
                        <ChevronDown size={18} />
                      </button>
                      <button onClick={() => setEditingSlide({ ...slide })} style={styles.iconBtn}>
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteSlide(slide.id)}
                        style={{ ...styles.iconBtn, ...styles.deleteBtn }}
                        disabled={carouselSlides.length <= 1}
                        title={carouselSlides.length <= 1 ? 'Keep at least one slide' : 'Delete'}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Edit Slide Modal */}
          {editingSlide && (
            <div style={styles.modal}>
              <div style={styles.modalContent}>
                <div style={styles.modalHeader}>
                  <h3 style={styles.modalTitle}>Edit Slide</h3>
                  <button onClick={() => setEditingSlide(null)} style={styles.modalClose}>
                    <X size={24} />
                  </button>
                </div>

                <div style={styles.eventForm}>
                  <div style={styles.imagePreview}>
                    <img src={editingSlide.image} alt={editingSlide.title} style={styles.previewImg} />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Image URL *</label>
                    <input
                      type="url"
                      value={editingSlide.image}
                      onChange={(e) => setEditingSlide({ ...editingSlide, image: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Title *</label>
                    <input
                      type="text"
                      value={editingSlide.title}
                      onChange={(e) => setEditingSlide({ ...editingSlide, title: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Subtitle</label>
                    <input
                      type="text"
                      value={editingSlide.subtitle}
                      onChange={(e) => setEditingSlide({ ...editingSlide, subtitle: e.target.value })}
                      style={styles.input}
                    />
                  </div>

                  <div style={styles.formActions}>
                    <button type="button" onClick={() => setEditingSlide(null)} style={styles.cancelBtn}>
                      Cancel
                    </button>
                    <button type="button" onClick={handleUpdateSlide} style={styles.saveBtn} disabled={isBusy}>
                      <Check size={18} />
                      {isBusy ? 'Saving…' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  app: {
    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F8FBFD',
    color: '#2C3E50',
  },
  
  // Header
  header: {
    backgroundColor: '#FFFFFF',
    borderBottom: '1px solid #E8F1F8',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(74, 144, 217, 0.06)',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  logoImage: {
    height: '40px',
    width: 'auto',
    maxWidth: '280px',
    objectFit: 'contain',
  },
  nav: {
    display: 'flex',
    gap: '8px',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    color: '#5A6C7D',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  navButtonActive: {
    backgroundColor: '#EBF4FC',
    color: '#4A90D9',
  },
  
  // Main
  main: {
    flex: 1,
  },
  noticeBanner: {
    backgroundColor: '#F0F4F8',
    border: '1px solid #D7E5F2',
    color: '#1A365D',
    borderRadius: '12px',
    padding: '12px 14px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  
  // Footer
  footer: {
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E8F1F8',
    padding: '24px',
    textAlign: 'center',
  },
  footerText: {
    color: '#8FA3B8',
    fontSize: '14px',
    margin: 0,
  },
  
  // Home Page
  homePage: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
  },
  
  // Carousel
  carouselContainer: {
    position: 'relative',
    borderRadius: '20px',
    overflow: 'hidden',
    marginTop: '24px',
    boxShadow: '0 12px 40px rgba(74, 144, 217, 0.15)',
  },
  carouselSlide: {
    width: '100%',
    height: '500px',
    backgroundColor: '#f0f4f8',
    transition: 'opacity 0.3s ease',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  carouselImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  carouselContent: {
    textAlign: 'center',
    color: '#FFFFFF',
    padding: '40px',
  },
  carouselTitle: {
    fontSize: '52px',
    fontWeight: '700',
    marginBottom: '16px',
    textShadow: '0 2px 20px rgba(0,0,0,0.2)',
    letterSpacing: '-1px',
  },
  carouselSubtitle: {
    fontSize: '22px',
    fontWeight: '400',
    opacity: 0.95,
    marginBottom: '32px',
  },
  carouselCTA: {
    padding: '16px 36px',
    backgroundColor: '#FFFFFF',
    color: '#4A90D9',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
  },
  carouselArrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '50%',
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4A90D9',
    boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
    transition: 'all 0.2s ease',
  },
  carouselDots: {
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '10px',
  },
  carouselDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    border: '2px solid #FFFFFF',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  carouselDotActive: {
    backgroundColor: '#FFFFFF',
    transform: 'scale(1.2)',
  },
  
  // Info Section
  infoSection: {
    padding: '60px 0',
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    padding: '36px',
    borderRadius: '16px',
    textAlign: 'center',
    border: '1px solid #E8F1F8',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(74, 144, 217, 0.05)',
  },
  infoIcon: {
    fontSize: '42px',
    marginBottom: '20px',
  },
  infoTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: '12px',
  },
  infoText: {
    fontSize: '15px',
    color: '#6B7C8D',
    lineHeight: '1.6',
    margin: 0,
  },
  
  // Events Preview
  eventsPreview: {
    paddingTop: '50px',
    paddingBottom: '60px',
  },
  sectionTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2C3E50',
    marginBottom: '28px',
    letterSpacing: '-0.5px',
  },
  eventsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '20px',
    marginBottom: '32px',
  },
  eventPreviewCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '14px',
    overflow: 'hidden',
    border: '1px solid #E8F1F8',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 15px rgba(74, 144, 217, 0.05)',
  },
  eventColorBar: {
    height: '5px',
  },
  eventPreviewContent: {
    padding: '20px',
  },
  eventPreviewTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: '8px',
  },
  eventPreviewDate: {
    fontSize: '14px',
    color: '#4A90D9',
    fontWeight: '500',
    marginBottom: '8px',
  },
  eventPreviewDesc: {
    fontSize: '14px',
    color: '#7A8B9C',
    margin: 0,
    lineHeight: '1.5',
  },
  viewAllBtn: {
    display: 'block',
    margin: '0 auto',
    padding: '14px 32px',
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  // Calendar Page
  calendarPage: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  calendarHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
  },
  calendarNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  calendarNavBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    border: '1px solid #E8F1F8',
    backgroundColor: '#FFFFFF',
    color: '#4A90D9',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  calendarMonth: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#2C3E50',
    minWidth: '260px',
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
  todayBtn: {
    padding: '10px 24px',
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    backgroundColor: '#FFFFFF',
    padding: '20px',
    borderRadius: '16px',
    border: '1px solid #E8F1F8',
    boxShadow: '0 4px 20px rgba(74, 144, 217, 0.06)',
  },
  calendarWeekday: {
    padding: '16px 8px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#8FA3B8',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  calendarDay: {
    minHeight: '100px',
    padding: '10px',
    backgroundColor: '#FAFCFE',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '2px solid transparent',
  },
  calendarDayEmpty: {
    minHeight: '100px',
    backgroundColor: 'transparent',
  },
  calendarDayToday: {
    backgroundColor: '#EBF4FC',
    border: '2px solid #4A90D9',
  },
  calendarDaySelected: {
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
  },
  dayNumber: {
    fontSize: '15px',
    fontWeight: '600',
    display: 'block',
    marginBottom: '6px',
  },
  dayEventsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  dayEvent: {
    fontSize: '11px',
    padding: '3px 6px',
    borderRadius: '4px',
    color: '#FFFFFF',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: '500',
  },
  moreEvents: {
    fontSize: '11px',
    color: '#8FA3B8',
    fontWeight: '500',
  },
  selectedDatePanel: {
    marginTop: '28px',
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    padding: '28px',
    border: '1px solid #E8F1F8',
    boxShadow: '0 4px 20px rgba(74, 144, 217, 0.06)',
  },
  selectedDateTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2C3E50',
    marginBottom: '20px',
    letterSpacing: '-0.3px',
  },
  selectedDateEvents: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  selectedEventCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#FAFCFE',
    borderRadius: '12px',
    border: '1px solid #E8F1F8',
  },
  selectedEventColor: {
    width: '5px',
    height: '100%',
    minHeight: '50px',
    borderRadius: '3px',
  },
  selectedEventInfo: {
    flex: 1,
  },
  selectedEventTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: '6px',
  },
  selectedEventDesc: {
    fontSize: '14px',
    color: '#7A8B9C',
    margin: 0,
    lineHeight: '1.5',
  },
  selectedEventTime: {
    fontSize: '13px',
    color: '#4A90D9',
    fontWeight: '500',
    margin: '0 0 4px 0',
  },
  noEvents: {
    color: '#8FA3B8',
    fontSize: '15px',
    textAlign: 'center',
    padding: '24px',
  },
  
  // Admin Page - Login
  loginContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 200px)',
    padding: '40px 24px',
  },
  loginCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    padding: '48px',
    width: '100%',
    maxWidth: '420px',
    border: '1px solid #E8F1F8',
    boxShadow: '0 12px 40px rgba(74, 144, 217, 0.1)',
  },
  loginHeader: {
    textAlign: 'center',
    marginBottom: '36px',
  },
  loginTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#2C3E50',
    marginTop: '20px',
    marginBottom: '10px',
    letterSpacing: '-0.5px',
  },
  loginSubtitle: {
    fontSize: '15px',
    color: '#7A8B9C',
    margin: 0,
  },
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
  },
  timeRow: {
    display: 'flex',
    gap: '16px',
  },
  inputLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4A5568',
  },
  input: {
    padding: '14px 16px',
    borderRadius: '10px',
    border: '2px solid #E8F1F8',
    fontSize: '15px',
    transition: 'all 0.2s ease',
    outline: 'none',
    backgroundColor: '#FAFCFE',
  },
  passwordContainer: {
    position: 'relative',
  },
  passwordToggle: {
    position: 'absolute',
    right: '14px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8FA3B8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loginError: {
    color: '#E74C3C',
    fontSize: '14px',
    textAlign: 'center',
    margin: 0,
    padding: '12px',
    backgroundColor: '#FEF2F2',
    borderRadius: '8px',
  },
  loginBtn: {
    padding: '16px',
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
  },
  demoCredentials: {
    marginTop: '28px',
    padding: '16px',
    backgroundColor: '#EBF4FC',
    borderRadius: '10px',
    textAlign: 'center',
  },
  demoText: {
    fontSize: '13px',
    color: '#5A6C7D',
    margin: '0 0 6px 0',
  },
  demoCode: {
    fontSize: '14px',
    color: '#4A90D9',
    fontWeight: '600',
  },
  
  // Admin Dashboard
  adminContainer: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  adminHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
  },
  adminTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '26px',
    fontWeight: '700',
    color: '#2C3E50',
    letterSpacing: '-0.5px',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    backgroundColor: '#FFFFFF',
    color: '#E74C3C',
    border: '2px solid #E74C3C',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  addEventBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '16px 28px',
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '28px',
    transition: 'all 0.2s ease',
  },
  
  // Modal
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(44, 62, 80, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px',
    backdropFilter: 'blur(4px)',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 28px',
    borderBottom: '1px solid #E8F1F8',
  },
  modalTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#2C3E50',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8FA3B8',
    display: 'flex',
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
  },
  eventForm: {
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  colorPicker: {
    display: 'flex',
    gap: '10px',
  },
  colorOption: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: '3px solid transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  colorOptionSelected: {
    borderColor: '#2C3E50',
    transform: 'scale(1.15)',
  },
  formActions: {
    display: 'flex',
    gap: '14px',
    marginTop: '12px',
  },
  cancelBtn: {
    flex: 1,
    padding: '14px',
    backgroundColor: '#F8FBFD',
    color: '#5A6C7D',
    border: '2px solid #E8F1F8',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  submitBtn: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  // Events List
  eventsList: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #E8F1F8',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(74, 144, 217, 0.06)',
  },
  eventsListTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#2C3E50',
    padding: '20px 24px',
    margin: 0,
    borderBottom: '1px solid #E8F1F8',
    backgroundColor: '#FAFCFE',
  },
  noEventsAdmin: {
    padding: '60px 24px',
    textAlign: 'center',
    color: '#8FA3B8',
    fontSize: '15px',
  },
  eventsTable: {
    padding: '12px',
  },
  eventRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '8px',
    backgroundColor: '#FAFCFE',
    border: '1px solid #E8F1F8',
    transition: 'all 0.2s ease',
  },
  eventRowColor: {
    width: '5px',
    height: '60px',
    borderRadius: '3px',
    marginRight: '18px',
  },
  eventRowInfo: {
    flex: 1,
  },
  eventRowTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: '4px',
  },
  eventRowDate: {
    fontSize: '14px',
    color: '#4A90D9',
    fontWeight: '500',
    marginBottom: '4px',
  },
  eventRowDesc: {
    fontSize: '14px',
    color: '#7A8B9C',
    margin: 0,
  },
  eventRowActions: {
    display: 'flex',
    gap: '10px',
  },
  editBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid #E8F1F8',
    backgroundColor: '#FFFFFF',
    color: '#4A90D9',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  deleteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid #E8F1F8',
    backgroundColor: '#FFFFFF',
    color: '#E74C3C',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  // Tab Navigation
  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '24px',
    backgroundColor: '#FFFFFF',
    padding: '8px',
    borderRadius: '14px',
    border: '1px solid #E8F1F8',
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '10px',
    backgroundColor: 'transparent',
    color: '#5A6C7D',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    flex: 1,
    justifyContent: 'center',
  },
  tabActive: {
    backgroundColor: '#4A90D9',
    color: '#FFFFFF',
  },
  
  // Carousel Management
  slideRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '8px',
    backgroundColor: '#FAFCFE',
    border: '1px solid #E8F1F8',
    transition: 'all 0.2s ease',
  },
  slideOrderBtns: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: '16px',
  },
  orderBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: '1px solid #E8F1F8',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    color: '#5A6C7D',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  slideOrder: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#8FA3B8',
    padding: '4px 0',
  },
  slideThumb: {
    width: '120px',
    height: '70px',
    borderRadius: '8px',
    overflow: 'hidden',
    marginRight: '18px',
    flexShrink: 0,
  },
  slideThumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  slideInfo: {
    flex: 1,
    minWidth: 0,
  },
  slideUrl: {
    fontSize: '12px',
    color: '#A0B0C0',
    margin: '4px 0 0 0',
    fontFamily: 'monospace',
  },
  imagePreview: {
    width: '100%',
    height: '150px',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: '#F0F4F8',
    marginBottom: '8px',
  },
  previewImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  inputHint: {
    fontSize: '12px',
    color: '#8FA3B8',
    marginTop: '6px',
    marginBottom: 0,
  },
};
